import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { IconButton, TextField, Tooltip } from '@mui/material';
import { useId, useState } from 'react';
import { tableTypeLabel } from '../../../utils/tableTypeLabel';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { AddViewPicker } from './AddViewPicker';
import {
  addTabs,
  canAddTabs,
  MAX_NAME_LENGTH,
  MAX_TABS,
  PickableView,
  removeTab,
  renameTab,
  reorderTabs,
  TabRow,
  viewNameIn
} from './scheduleTabs';
import { viewTypeOf } from './viewKey';

export interface ReportTabsEditorProps {
  rows: TabRow[];
  views: PickableView[];
  disabled: boolean;
  onChange: (rows: TabRow[]) => void;
}

export const ReportTabsEditor = ({ rows, views, disabled, onChange }: ReportTabsEditorProps) => {
  const labelId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const nameOf = (viewKey: string) => viewNameIn(views, viewKey);

  const handleDragEnd = (result: DropResult) => onChange(reorderTabs(rows, result));

  return (
    <div className="mb-3">
      <div id={labelId}>
        <TextBlock
          size="xs"
          color="grey6"
          className="font-medium mb-1">
          Report tabs{' '}
          <TextSpan color="grey4">
            {rows.length} of {MAX_TABS}
          </TextSpan>
        </TextBlock>
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="report-tabs">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              role="group"
              aria-labelledby={labelId}
              data-testid="report-tabs">
              {rows.map((row, index) => (
                <Draggable
                  key={row.viewKey}
                  draggableId={row.viewKey}
                  index={index}
                  isDragDisabled={disabled || rows.length === 1}>
                  {(drag) => (
                    <div
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      className="flex items-start gap-2 border border-gray-200 rounded px-2 py-2 mb-2 bg-white"
                      data-testid={`report-tab-${index}`}>
                      <span
                        {...drag.dragHandleProps}
                        aria-label={`Reorder ${row.tabName || nameOf(row.viewKey)}`}
                        className="pt-1 text-gray-400">
                        <DragIndicatorIcon fontSize="small" />
                      </span>
                      <TextSpan
                        size="xs"
                        color="grey5"
                        className="pt-1 w-4">
                        {index + 1}
                      </TextSpan>
                      <div className="grow">
                        <TextField
                          size="small"
                          fullWidth
                          value={row.tabName}
                          disabled={disabled}
                          inputProps={{ 'aria-label': `Tab name ${index + 1}`, maxLength: MAX_NAME_LENGTH }}
                          onChange={(e) => onChange(renameTab(rows, index, e.target.value))}
                        />
                        <TextBlock
                          size="xs"
                          color="grey6"
                          className="mt-1">
                          {tableTypeLabel(viewTypeOf(row.viewKey))} · {nameOf(row.viewKey)}
                        </TextBlock>
                      </div>
                      <Tooltip title={rows.length === 1 ? 'A report needs at least one view' : 'Remove from report'}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remove tab ${index + 1}`}
                            disabled={disabled || rows.length === 1}
                            onClick={() => onChange(removeTab(rows, index))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="text-xs text-indigo-600 whitespace-nowrap disabled:text-gray-400"
          disabled={disabled || !canAddTabs(rows)}
          aria-haspopup="dialog"
          onClick={(e) => setAnchorEl(e.currentTarget)}>
          + Add view
        </button>
        <TextSpan
          size="xs"
          color="grey5">
          {canAddTabs(rows)
            ? 'Optional. Combine other views from this page into one report.'
            : `A report holds up to ${MAX_TABS} views.`}
        </TextSpan>
      </div>
      <AddViewPicker
        anchorEl={anchorEl}
        views={views}
        includedKeys={rows.map((row) => row.viewKey)}
        onAdd={(picked) => onChange(addTabs(rows, picked))}
        onClose={() => setAnchorEl(null)}
      />
    </div>
  );
};
